/**
 * Joining voice from a screen: asks for a channel password when needed,
 * and says calmly why a join failed ("Couldn't join Lobby: wrong
 * password").
 */
import type { Snowflake } from '@neosocial/protocol';
import { tr } from '../i18n';
import { useData } from '../store/data';
import { activeGuildId } from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import {
  joinedWithDefaultMic,
  joinVoice,
  leaveVoice,
  onLostAccessMove,
  VoiceJoinError,
} from '../voice/controller';
import type { VoiceSession } from '../voice/machine';
import { useVoice } from '../voice/store';
import { promptDialog, toast } from '../ui/overlay';
import { flagOn, onConfigChange } from '../store/config';
import { isApiRequestError } from '../lib/api';
import { errorText, runAction } from '../mod/actions';
import { moveMemberToChannel, moveRoute, type MoveRoute } from '../store/moveMember';
import type { ClientConfig } from '@neosocial/protocol';

/**
 * The operator turned voice off while you were in a room: leave it calmly
 * and say why (the server refuses new voice tokens anyway).
 */
export function leaveIfVoiceTurnedOff(next: ClientConfig, prev: ClientConfig | null): boolean {
  if (flagOn('voice', next) || !flagOn('voice', prev)) return false;
  if (useVoice.getState().session.status === 'idle') return false;
  leaveVoice();
  toast(tr().t('cfg.voiceEnded'), { icon: 'headset', tone: 'warn' });
  return true;
}
onConfigChange(leaveIfVoiceTurnedOff);

/**
 * A role or overwrite change took your channel away and comms-voice moved
 * you to the default channel: say so, in words (QA 2026-10-28: Bahar lost
 * Connect to Room 2 and only heard the "move" sound; the dock just said
 * "Voice connected · Lobby"). With nowhere to go, the room and the dock say
 * "You no longer have access to …" instead (close 4412).
 */
export function sayLostAccessMove(from: Snowflake, to: Snowflake): void {
  const { t } = tr();
  const name = (id: Snowflake) => useData.getState().channels[id]?.name ?? '';
  toast(t('ns.voice.movedLostAccess', { from: name(from), to: name(to) }), {
    icon: 'lock',
    tone: 'warn',
    duration: 8000,
  });
}
onLostAccessMove(({ from, to }) => sayLostAccessMove(from, to));

/**
 * Your session's channel changed without you choosing it (a moderator's
 * "Move to…", a channel ban or a removed channel moving you to the landing
 * channel): when the open view is the voice room you were moved out of,
 * it follows you to the new one, as Discord's does. QA 2026-10-28 round 3:
 * after a move, B's main area still said "You're not in Lobby · Join voice"
 * while B's dock said "Voice connected · Room 2". A move you make yourself
 * opens the new room first (`joinVoiceFromUi`), so nothing happens then;
 * any other view (a text channel, Home) stays where it is. A disconnect
 * (no new channel) leaves the room you were in on screen, which says
 * "Disconnected from …" and why, with "Join again".
 */
export function followForcedMove(
  next: Pick<VoiceSession, 'channelId'>,
  prev: Pick<VoiceSession, 'channelId'>,
): boolean {
  const from = prev.channelId;
  const to = next.channelId;
  if (!from || !to || from === to) return false;
  const view = useUi.getState().hub;
  if (view?.kind !== 'voice' || view.channelId !== from) return false;
  goHub({ kind: 'voice', channelId: to });
  return true;
}
useVoice.subscribe((s, prev) => {
  if (s.session.channelId !== prev.session.channelId) followForcedMove(s.session, prev.session);
});

/**
 * Joins a voice channel from Hub (opening its room unless `navigate` is
 * false). A channel with a password asks for it (the server and
 * comms-voice check it); a wrong one asks again with the reason, up to
 * three tries, then says calmly that it couldn't join.
 */
export async function joinVoiceFromUi(
  channelId: Snowflake,
  o: { navigate?: boolean } = {},
): Promise<boolean> {
  if (!flagOn('voice')) {
    toast(tr().t('cfg.off.voice'), { icon: 'headset', tone: 'warn' });
    return false;
  }
  const d = useData.getState();
  const gid = d.channels[channelId]?.guild_id ?? activeGuildId(d);
  const name = d.channels[channelId]?.name ?? '';
  if (!gid) return false;
  const before = useUi.getState().hub;
  if (o.navigate !== false) goHub({ kind: 'voice', channelId });
  /** A cancelled password prompt puts back what you were looking at (HV-B16). */
  const putBack = () => {
    const now = useUi.getState().hub;
    if (o.navigate !== false && before && now?.kind === 'voice' && now.channelId === channelId)
      goHub(before);
  };
  let password: string | undefined;
  let error: string | undefined;
  const t = tr().t;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      await joinVoice(channelId, { guildId: gid, password });
      // the saved microphone is gone: say the default one is used (HV-B07, ST-B05), once
      if (joinedWithDefaultMic()) toast(t('set.voice.micMissing'), { icon: 'mic', tone: 'warn' });
      return true;
    } catch (e) {
      const code = e instanceof VoiceJoinError ? e.code : 'unknown';
      if (code === 'password_required' || (code === 'bad_password' && attempt < 3)) {
        if (code === 'bad_password') error = t('ns.voice.pwWrong');
        const p = await promptDialog({
          title: t('ns.voice.pwTitle', { channel: name }),
          body: t('ns.voice.pwBody'),
          inputLabel: t('ns.voice.pwLabel'),
          inputType: 'password',
          confirmLabel: t('c.join'),
          icon: 'lock',
          error,
        });
        if (p === null) {
          putBack();
          return false;
        }
        password = p;
        continue;
      }
      // A refusal the app has words for (a staff limit with its end date, voice
      // turned off, maintenance) says that, not "something went wrong" (CO-BUG-16).
      const refusal = e instanceof VoiceJoinError ? e.refusal : undefined;
      const known =
        code === 'unknown' && isApiRequestError(refusal) && tr().has(`ns.err.${refusal.code}`);
      toast(
        known
          ? t('ns.voice.couldNotBecause', { channel: `⁨${name}⁩`, why: errorText(refusal) })
          : t('ns.voice.couldNot', {
              channel: `⁨${name}⁩`,
              why: t(`ns.voice.err.${code}`, { channel: name }),
            }),
        { icon: 'alert', tone: 'danger' },
      );
      return false;
    }
  }
  return false;
}

/**
 * Moves someone, or you, to a voice channel from a screen: the sidebar's
 * drag and drop and "Move to…" (`store/moveMember.ts` decides the route).
 * You, connected in this app: the voice engine, as a click on the channel
 * does (a password asked for, a refusal said in a toast), without opening
 * the room (a room you were looking at follows you, `followForcedMove`).
 * Anyone else, or you on another device: `moveMemberToChannel` (the
 * member PATCH), with a step-up when the server asks; its refusal is a
 * toast with the server's reason (`ns.err.*`) unless `quiet`, which leaves
 * it to the caller (`error`).
 */
export async function moveMemberFromUi(
  guildId: Snowflake,
  userId: Snowflake,
  channelId: Snowflake,
  o: { quiet?: boolean } = {},
): Promise<{ ok: boolean; route: MoveRoute; error?: unknown }> {
  const route = moveRoute(useData.getState(), guildId, userId, channelId);
  if (route === 'none') return { ok: false, route };
  if (route === 'engine')
    return { ok: await joinVoiceFromUi(channelId, { navigate: false }), route };
  const r = await runAction(() => moveMemberToChannel(guildId, userId, channelId), {
    quiet: o.quiet,
  });
  return r.ok ? { ok: true, route } : { ok: false, route, error: r.error };
}
